import { WatchfaceService } from '@/services/watchface';
import { getImageBase64, getImageSize } from '@/utils/fs/image.utils';
import { defineStore } from 'pinia';
import { ElectronApi } from '@/services/electron.api';
import { fileToMd5 } from '@/utils/md5.utils';

const _generateImageBasename = async (options: {
  imgPath: string;
  md5?: boolean;
  dirname?: string;
}) => {
  const path = ElectronApi.path;
  if (!path) return;

  const { imgPath, md5, dirname } = options;

  // md5
  const _basename = path.basename(imgPath);
  const _extname = path.extname(imgPath);
  const _basename2 = _basename.replace(_extname, '');
  const prefix = /^[0-9]{1,}$/g.test(_basename2) ? _basename2 : '';
  const filename = md5
    ? (prefix ? _basename2 + '_' : '') +
      (await fileToMd5(imgPath, true)) +
      _extname
    : _basename;
  // 限制dirname为10位长度
  const name = dirname ? dirname.slice(0, 20) + '/' + filename : filename;
  return name;
};

export const useImagesStore = defineStore('images-store', {
  state: () => ({
    imgs: [] as StoreResourceItem[]
  }),

  getters: {
    /**
     * 获取单个图片对象
     * @param state
     * @returns
     */
    image(state) {
      return (basename: string) => {
        return state.imgs.find((item) => item.basename == basename);
      };
    },
    skuArr(state) {
      return state.imgs.filter((item) => item.basename.startsWith('_sku'));
    },

    imagesMap(state) {
      const map: Record<string, StoreResourceItem> = {};
      state.imgs.forEach((item) => {
        map[item.basename] = item;
      });
      return map;
    },

    getUsingImages(state) {
      return (
        layers: (StoreLayerItem | StoreThemeItem)[],
        options?: {
          includeSku?: boolean;
        }
      ) => {
        const images: {
          base64: string;
          basename: string;
        }[] = [];

        let layerstr = '';
        try {
          layerstr = JSON.stringify(layers);
        } catch (error) {
          console.log(error);
        }

        const includeSku = !!options?.includeSku;
        state.imgs.forEach((item) => {
          if (
            !layerstr ||
            layerstr.includes(item.basename) ||
            (includeSku && item.basename.includes('_sku')) ||
            ['_emptyImage.png', '_defaultBg.png'].includes(item.basename)
          ) {
            images.push({ base64: item.value ?? '', basename: item.basename });
          }
        });
        return images;
      };
    }
  },

  actions: {
    /**
     * 同步本地目录中的图片
     */
    async sync(imgs?: StoreResourceItem[]) {
      const res =
        imgs ?? (await new WatchfaceService().resource.getImagesData());
      if (res) {
        // _imgDomsCache.clear();
        this.$state.imgs = res;
      }
    },

    /**
     * 添加图片，返回basename; basename = [dirname?]/basename
     * @param imgPath 图片实际路径
     * @param dirname [可选] 图片上一级目录，当为空时，basename为图片名称
     * @returns 返回在store中的图片名称，即maml中使用的名称
     */
    async uploadImage(
      imgPath: string,
      options?: {
        dirname?: string;
        md5?: boolean;
        basename?: string;
      }
    ) {
      const { dirname, md5, basename: basename0 } = options ?? {};
      const path = ElectronApi.path;
      if (!path) return;

      const name =
        basename0 ?? (await _generateImageBasename({ imgPath, md5, dirname }));
      if (!name) return;

      // 如果存在，则不处理
      const existsOne = this.image(name);
      if (existsOne) return existsOne;

      const base64 = await getImageBase64(imgPath);

      // 获取大小
      const { width = 0, height = 0 } =
        (await getImageSize(undefined, base64)) ?? {};

      // 存在时，不删除原来的，直接返回（上面代码）
      // 如果存在，则删除之
      // const index = this.imgs.findIndex((item) => item.basename == name);
      // if (index > -1) this.imgs.splice(index, 1);

      this.imgs.push({
        realpath: imgPath,
        basename: name,
        value: base64,
        width,
        height
      });

      return this.image(name);
    },
    /**
     * 批量上传文件
     * @param imgPath 图片实际路径
     * @param dirname [可选] 图片上一级目录，当为空时，basename为图片名称
     * @returns 返回在store中的图片名称，即maml中使用的名称
     */
    async uploadImages(
      imgPaths: string[],
      options?: {
        dirname?: string;
        md5?: boolean;
      }
    ) {
      const { dirname, md5 } = options ?? {};
      const path = ElectronApi.path;

      const basenames = imgPaths.map((imgPath) => {
        return {
          imgPath,
          basename: ''
        };
      });

      if (!path) return;

      let index = 0;
      const step = 6;
      const items: StoreResourceItem[] = [];
      while (index < imgPaths.length) {
        const promises: Promise<undefined>[] = [];
        for (let i = index; i < step + index; i++) {
          const imgPath = imgPaths[i];
          if (!imgPath) continue;

          promises.push(
            (async () => {
              const basename = await _generateImageBasename({
                imgPath,
                md5,
                dirname
              });
              if (!basename) return;

              const base64 = await getImageBase64(imgPath);
              // 获取大小
              const { width = 0, height = 0 } =
                (await getImageSize(undefined, base64)) ?? {};
              const item: StoreResourceItem = {
                realpath: imgPath,
                basename,
                value: base64,
                width,
                height
              };
              items.push(item);
              const item2 = basenames.find((item) => item.imgPath == imgPath);
              if (item2) item2.basename = basename;
            })()
          );

          await Promise.all(promises);
        }
        index += step;
      }
      items.forEach((item) => {
        if (!item) return;
        if (this.image(item.basename)) return;
        this.imgs.push({ ...item });
      });

      return basenames.map((item) => item.basename);
    },

    /**
     * 直接添加图片
     * @param item
     * @returns
     */
    insertImage(item: StoreResourceItem) {
      if (this.image(item.basename)) return;
      this.imgs.push({ ...item });
    },
    /**
     * 直接添加图片
     * @param item
     * @returns
     */
    async deleteImage(basename: string) {
      this.imgs = this.imgs.filter((item) => item.basename != basename);
    },

    addEmptyImage() {
      const basename = '_emptyImage.png';
      if (this.image(basename)?.basename) return basename;

      // 创建一个空的image
      const canvas = document.createElement('canvas');
      canvas.width = 16;
      canvas.height = 16;
      const res = canvas.toDataURL();
      this.insertImage({
        basename,
        value: res,
        realpath: '',
        width: 16,
        height: 16
      });
      return basename;
    },
    addBgImage(data: { width: number; height: number; color?: string }) {
      const basename = '_defaultBg.png';
      if (this.image(basename)?.basename) return basename;
      const { width, height, color = 'black' } = data;
      // 创建一个空的image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.rect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = color;
        ctx.fill();
      }
      const res = canvas.toDataURL();
      this.insertImage({
        basename,
        value: res,
        realpath: '',
        width,
        height
      });
      return basename;
    }
  }
});
